import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from '@/common/containers/FirstTimeQuestion/styles.less';
import Button from '@mui/material/Button';
import cx from 'classnames';
import {
  ImmediateTasks,
  MainTasks,
  AssetTasks,
} from '@/common/containers/Forms/index';
import {
  TASK_MODULES,
  getTaskModuleLabelKey,
} from '@/common/constants/constants';
import LegalTasks from '@/common/containers/Forms/LegalTasks';
import { Notification } from '@/common/utils/index';
import shortid from 'shortid';
import { PromptSaveModal } from '@/common/containers/Modals/index';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { useSubmitQuestionMutation } from '@/entities/decedent';

const FirstTimeQuestion = ({ className, bodyClassName, module }) => {
  const decedentId = useCurrentDecedentId();
  const submitQuestionMutation = useSubmitQuestionMutation();
  const formRef = useRef();
  const [formState, setFormState] = useState({});
  const submitRef = useRef(() => {});
  const [saveVersion, setSaveVersion] = useState(shortid.generate());
  const { t } = useTranslation();
  const moduleLabel = t(getTaskModuleLabelKey(module));
  const loading = submitQuestionMutation.isPending;

  useEffect(() => {
    window.onbeforeunload = formState.isDirty ? () => true : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [formState.isDirty, loading]);

  const onSubmit = useCallback(
    (form, isSave = false) => {
      return submitQuestionMutation.mutateAsync({
        decedentId: decedentId,
        answers: form,
        section: module,
        isProgressSaving: isSave,
      });
    },
    [decedentId, module, submitQuestionMutation]
  );

  const onSaveProgress = useCallback(async () => {
    const values = formRef.current?.getShapedValues();
    // TODO: add user notification on false condition;
    if (!values) return;
    if (!values.length) {
      Notification.info(t('Please fill the form'));
      return;
    }

    await onSubmit(values, true);
    setSaveVersion(shortid.generate());
    Notification.success(t('Your progress is saved'));
  }, [onSubmit, t]);

  const Form = useMemo(() => {
    const props = {
      onSubmit,
      formId: 'FirstTimeQuestion',
      formRef,
      key: saveVersion,
      onFormStateChange: (form, submitForm) => {
        setFormState(form);
        submitRef.current = submitForm;
      },
    };
    const forms = {
      [TASK_MODULES.IMMEDIATE]: ImmediateTasks,
      [TASK_MODULES.MAIN]: MainTasks,
      [TASK_MODULES.ASSET]: AssetTasks,
      [TASK_MODULES.LEGAL]: LegalTasks,
    };
    const Component = forms[module];
    return Component ? React.cloneElement(<Component />, props) : null;
  }, [module, onSubmit, saveVersion]);

  return (
    <>
      <PromptSaveModal
        when={!!formState.isDirty && !loading}
        onSave={onSaveProgress}
        title={t('Save progress?')}
        description={t('Do you want to save the progress before you leave?')}
      />
      <div className={cx(styles.section, className)}>
        <div className={cx(styles.sectionBody, bodyClassName)}>
          <h3 className={styles.sectionBodyTitle}>
            {t('{{module}} questionnaire', { module: moduleLabel })}
          </h3>
          <p className={styles.sectionBodyLabel}>
            <Trans
              i18nKey="Please answer the following questions to generate a personalised list of tasks. If you do not know the answer or are unsure, select &quot;not sure&quot;. You will be able to come back to this questionnaire later to review and provide a definitive 'Yes' / 'No' answer when you are able to in order to finalize the <1>{{module}}</1> list."
              values={{ module: moduleLabel }}
            >
              <b />
            </Trans>
          </p>
          <div className={styles.sectionBodyForm}>{Form}</div>
        </div>
        <div className={styles.sectionFooter}>
          <Button
            variant="outlined"
            color="success"
            onClick={onSaveProgress}
            loading={loading}
          >
            {t('Save progress')}
          </Button>
          <Button
            variant="contained"
            type="success"
            form="FirstTimeQuestion"
            loading={loading}
          >
            {t('Finish questionnaire')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default FirstTimeQuestion;
